import * as services from '@services/dependent'
import * as types from '@types'
import { resolveCustomFieldValues } from '@convenia/common-organisms/SystemFields/helpers'

export default {
  state: {
    dependents: [],
    options: {
      dependent: {
        dependentRelations: []
      }
    },
    customFields: {}
  },

  getters: {
    hasDependent: (state) => {
      return state.dependents.some(
        ({ id }) => typeof id === 'string'
      )
    }
  },

  mutations: {
    [types.DEPENDENT_RELATIONS_OPTIONS]: (state, options) => {
      state.options = { ...options }
    },

    [types.EMPLOYEE_SET_DEPENDENTS]: (state, dependents) => {
      state.dependents = [ ...dependents ]
    },

    [types.EMPLOYEE_ADD_DEPENDENT]: (state) => {
      state.dependents = [
        ...state.dependents,
        { id: new Date().getUTCMilliseconds(), files: [] }
      ]
    },

    [types.EMPLOYEE_SET_DEPENDENT]: (state, { data, id, previousId }) => {
      const index = state.dependents.findIndex(dependent =>
        dependent.id === (previousId || data?.id || id)
      )

      if (index >= 0)
        state.dependents.splice(index, 1, { files: [], ...data })
      else
        state.dependents.push({ ...data })
    },

    [types.EMPLOYEE_SET_DEPENDENT_FILES]: (state, { id, files }) => {
      const dependent = state.dependents.find(dependent => dependent.id === id)

      if (!dependent)
        return

      dependent.files = [ ...files ]
    },

    [types.EMPLOYEE_DELETE_DEPENDENT]: (state, dependent) => {
      state.dependents = state.dependents.filter(({ id }) => id !== dependent.id)
    },

    [types.EMPLOYEE_DEPENDENT_CUSTOM_FIELDS]: (state, data) => {
      state.customFields = { ...data }

      state.dependents = resolveCustomFieldValues(
        state.dependents,
        state.customFields?.dependent,
      )
    }
  },

  actions: {
    [types.DEPENDENT_RELATIONS_OPTIONS]: async ({ state, commit }) => {
      if (state.options.dependent.dependentRelations.length)
        return [ false, state.options ]

      const [ err, options ] = await services.getOptions()

      if (!err) commit(types.DEPENDENT_RELATIONS_OPTIONS, options)

      return [ err, options ]
    },

    [types.EMPLOYEE_DEPENDENTS]: async ({ getters, commit }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, dependents ] = await services.getDependents(params)

      if (!dependents?.length) commit(types.EMPLOYEE_ADD_DEPENDENT)
      else if (!err) commit(types.EMPLOYEE_SET_DEPENDENTS, dependents)

      return [ err, dependents ]
    },

    [types.EMPLOYEE_DEPENDENT_CUSTOM_FIELDS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, data ] = await services.getCustomFields(params)

      if (!err) commit(types.EMPLOYEE_DEPENDENT_CUSTOM_FIELDS, data)

      return [ err, data ]
    },

    [types.EMPLOYEE_SET_DEPENDENT]: async ({ state, commit, getters }, data) => {
      const { schema } = state.customFields.dependent || {}
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId, schema }

      const isCreating = typeof data?.id !== 'string'

      if (isCreating) {
        const [ err, dependent ] = await services.createDependent(data, params)

        if (!err) commit(types.EMPLOYEE_SET_DEPENDENT, { data: dependent, previousId: data?.id })

        return [ err, dependent ]
      }

      const [ err ] = await services.updateDependent(data, params)

      return [ err, data ]
    },

    [types.EMPLOYEE_SET_DEPENDENTS]: async ({ state, dispatch }, dependents) => {
      if (!dependents.length) return [ false, state.dependents ]

      const submitted = []
      const errors = []

      const results = await Promise.allSettled((dependents || [])
        .map(data => dispatch(types.EMPLOYEE_SET_DEPENDENT, data))
      ).catch(err => errors.push(err))

      results.forEach(({ value: [ err, contact ] = [] }) => err
        ? errors.push(err)
        : submitted.push(contact)
      )

      if (!errors.length) await dispatch(types.EMPLOYEE_DEPENDENTS)

      return [ errors, submitted ]
    },

    [types.EMPLOYEE_DELETE_DEPENDENT]: async ({ getters, commit }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err ] = await services.deleteDependent(data, params)

      if (!err) commit(types.EMPLOYEE_DELETE_DEPENDENT, data)

      return [ err, null ]
    },

    [types.EMPLOYEE_DEPENDENT_UPLOAD_FILES]: async ({ getters }, { id, files }) => {
      const employeeId = getters.getEmployeeUuid
      const params = { id, files, employeeId }

      const [ err, data ] = await services.uploadDependentFiles(params)

      if (err) return [ err, [] ]

      return [ err, data ]
    },
  }
}
